import React, { useState, useEffect, useMemo, useRef } from 'react';
import { IconX, IconMessageCircle, IconExclamationCircleFilled, IconLayoutSidebar } from '@tabler/icons-react';

import { Message, ConversationItem } from '../../@types/chat';
import { Icon, Button, Warning } from '../IDSK';
import Conversation from './Conversation';
import ConversationList from './ConversationList';
import NewMessage from './NewMessage';
import { useDocument } from '../../context/document';
import { useWebsockets, useListen } from '../../context/websockets';
import { useStoredState } from '../../hooks/use-stored-state';

export default function Popup({ sandbox = false }: { sandbox?: boolean } = {}) {
    const doc = useDocument();
    const ws = useWebsockets();
    const [chatEnabled, setChatEnabled] = useState<boolean|null>(sandbox ? true : null);
    const [showPopup, setShowPopup] = useStoredState('chat.show-popup', false);
    const [activeConversation, setActiveConversation] = useState<string|null>(null);
    const [conversationsLoaded, setConversationsLoaded] = useState(false);
    const [lastReadMessageId, setLastReadMessageId] = useStoredState<string|null>('chat.last-read-message', null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [conversations, setConversations] = useState<ConversationItem[]>([]);
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const [showSidebarOverlay, setShowSidebarOverlay] = useState(false);

    const activeConversationRef = useRef(activeConversation);
    const prevLostConnectionRef = useRef(ws.lostConnection);
    const isReconnectRef = useRef(false);
    const isLoadMoreConversationsRef = useRef(false);

    useEffect(() => {
        activeConversationRef.current = activeConversation;
    }, [activeConversation]);

    useEffect(() => {
        const wasLost = prevLostConnectionRef.current;
        prevLostConnectionRef.current = ws.lostConnection;

        if (wasLost && !ws.lostConnection) {
            isReconnectRef.current = true;
            ws.sendMessage('list', null);
        }
    }, [ws.lostConnection]);

    const isTyping = messages.length > 0 && messages[messages.length - 1].sender === 'USER';

    useEffect(() => {
        if (sandbox) return;

        const baseUrl = document.getElementById('chat-app')?.getAttribute('data-url') || '';

        fetch(`${baseUrl}/chat/enabled`)
            .then((res) => res.json())
            .then((data) => setChatEnabled(data.enabled))
            .catch(() => setChatEnabled(true));
    }, []);

    const conversation = useMemo(() => {
        return conversations.find((c) => c.id === activeConversation);
    }, [activeConversation, conversations]);

    /**
     * Handlers
     * */
    const onOpen = () => {
        setShowPopup(true);
    };

    const onClose = () => {
        setShowPopup(false);
    };

    const onLoadMore = () => {
        if(activeConversation === null) {
            return;
        }

        ws.sendMessage('load', {
            conversation: activeConversation,
            last_message: messages.length > 0 ? messages[0].id : '',
        });
    };

    const onNewChat = () => {
        const emptyConversation = conversations.find((c) => !c.title);

        if (emptyConversation) {
            setActiveConversation(emptyConversation.id);
            setShowSidebarOverlay(false);

            return;
        }

        ws.sendMessage('start', null);
    };

    const onCloseChat = (id: string) => {
        ws.sendMessage('close', id);
    };

    const onSelectConversation = (conversationId: string) => {
        setActiveConversation(conversationId);
        setShowSidebarOverlay(false);
    };

    const onLoadMoreConversations = () => {
        isLoadMoreConversationsRef.current = true;
        ws.sendMessage('list', { offset: conversations.length });
    };

    /**
     * Watchers
     * */
    useEffect(() => {
        if(!conversationsLoaded) return;

        setMessages([]);

        if(activeConversation === null) {
            return;
        }

        ws.sendMessage('load', {
            conversation: activeConversation,
            last_message: '',
        });
    }, [activeConversation, conversationsLoaded]);

    useEffect(() => {
        if(showPopup && messages.length > 1) {
            const lastMessage = messages[messages.length - 1];

            if(lastMessage.sender === 'BOT') {
                setLastReadMessageId(lastMessage.id);
                setHasUnreadMessages(false);
            }
        }
    }, [showPopup, messages]);

    useEffect(() => {
        if(messages.length <= 1) {
            return;
        }

        const lastMessage = messages[messages.length - 1];

        if(lastMessage.sender === 'BOT' && lastMessage.id !== lastReadMessageId && !showPopup) {
            setHasUnreadMessages(true);
        }
    }, [messages, lastReadMessageId, showPopup]);

    useEffect(() => {
        if(ws.isConnected) {
            ws.sendMessage('list', null);
        }
    }, [ws.isConnected]);

    /**
     * Listeners
     * */
    useListen('started', (id: string) => {
        setActiveConversation(id);
        setShowSidebarOverlay(false);
        ws.sendMessage('list', null);
    }, []);

    useListen('closed', (id: string) => {
        ws.sendMessage('list', null);

        if(activeConversation === id) {
            setActiveConversation(null);
        }
    }, [activeConversation]);

    useListen('loaded', (data: { messages: Message[], has_more: boolean }) => {
        setMessages((prev) => {
            const sorted = data.messages.reverse();

            const newMessages = sorted.filter((newMsg) => {
                return !prev.find((msg) => msg.id === newMsg.id);
            });

            return [...newMessages, ...prev];
        });

        setHasMoreMessages(data.has_more);
    }, []);

    useListen('new-message', (data: Message) => {
        setConversations((prev) => {
            const convos = prev.map((conv) => {
                if(conv.id === data.conversation_id) {
                    return { ...conv, date: data.date };
                }

                return conv;
            });

            // @ts-ignore
            return convos.sort((a, b) => new Date(b.date) - new Date(a.date));
        });

        if(data.conversation_id !== activeConversationRef.current) {
            return;
        }

        setMessages((prev) => {
            const isNew = !prev.find((msg) => msg.id === data.id);

            if(isNew) {
                return [...prev, data];
            }

            return prev;
        });
    }, []);

    useListen('listed', (data: { conversations: ConversationItem[], has_more: boolean }) => {
        setHasMoreConversations(data.has_more);

        if (isLoadMoreConversationsRef.current) {
            isLoadMoreConversationsRef.current = false;

            setConversations((prev) => {
                const existingIds = new Set(prev.map((c) => c.id));
                const newItems = data.conversations.filter((c) => !existingIds.has(c.id));

                return [...prev, ...newItems];
            });

            return;
        }

        setConversations(data.conversations);

        if (isReconnectRef.current) {
            isReconnectRef.current = false;
            const currentId = activeConversationRef.current;
            const stillExists = currentId && data.conversations.find((c) => c.id === currentId);

            if (stillExists) {
                ws.sendMessage('load', { conversation: currentId, last_message: '' });
                setMessages([]);
            } else if (data.conversations.length > 0) {
                setActiveConversation(data.conversations[0].id);
            }

            return;
        }

        if(data.conversations.length === 0) {
            ws.sendMessage('start', null);
        } else {
            setActiveConversation(data.conversations[0].id);
        }

        setConversationsLoaded(true);
    }, []);

    useListen('title-updated', (data: { conversation_id: string, title: string | null }) => {
        setConversations((prev) => {
            return prev.map((conv) => {
                if(conv.id === data.conversation_id) {
                    return { ...conv, title: data.title };
                }

                return conv;
            });
        });
    }, []);

    useListen('chat-enabled', (enabled: boolean) => {
        if (sandbox) return;

        setChatEnabled(enabled);
    }, []);

    if (chatEnabled === null || chatEnabled === false) {
        return null;
    }

    const showWindow = sandbox || showPopup;

    return <>
        {!sandbox && !showPopup && <Icon className="chat--popup--button" onClick={onOpen}>
            <IconMessageCircle />

            {hasUnreadMessages && <IconExclamationCircleFilled className="chat--popup--button-unread" />}
        </Icon>}

        {showWindow && <div className="chat--popup--window">
            <div className="chat--popup--main">
                <div className="chat--popup--header">
                    <div className="chat--popup--title">
                        <Icon className="chat--popup--sidebar-toggle" onClick={() => setShowSidebarOverlay(!showSidebarOverlay)} title="Zobraziť konverzácie">
                            <IconLayoutSidebar />
                        </Icon>

                        <div className="chat--popup--title-text">
                            <div className="govuk-heading-s">KyberAsistent</div>
                            {!!conversation?.title && <div className="chat--popup--title--label govuk-heading-s">{conversation.title}</div>}
                        </div>
                    </div>

                    {!sandbox && <div className="chat--popup--header-controls">
                        <Icon className="chat--popup--close" onClick={onClose} title="Zavrieť okno">
                            <IconX />
                        </Icon>
                    </div>}
                </div>

                <div className="idsk-header-web__tricolor" />

                {showSidebarOverlay && <>
                    <div className="chat--popup--sidebar-overlay-backdrop" onClick={() => setShowSidebarOverlay(false)} />
                    <div className="chat--popup--sidebar-overlay">
                        <ConversationList
                            conversations={conversations}
                            activeConversation={activeConversation}
                            hasMore={hasMoreConversations}
                            onSelect={onSelectConversation}
                            onNewChat={onNewChat}
                            onClose={onCloseChat}
                            onLoadMore={onLoadMoreConversations}
                        />
                    </div>
                </>}

                {activeConversation && <>
                    <Conversation messages={messages} hasMoreMessages={hasMoreMessages} isTyping={isTyping} onLoadMore={onLoadMore} />
                    <NewMessage disabled={isTyping} activeConversation={activeConversation} />
                </>}

                {!activeConversation && <>
                    <div className="chat--popup--no-conversation">
                        <div className="chat--popup--no-conversation--message">
                            Vyberte konverzáciu alebo začnite novú
                        </div>
                        <Button onClick={onNewChat}>Začať novú konverzáciu</Button>
                    </div>
                </>}

                {ws.lostConnection && <div className="chat--popup--lost-connection">
                    <Warning>
                        <div>Spojenie so serverom bolo prerušené. Čakajte prosím na obnovenie spojenia</div>
                        <Button onClick={() => ws.socket.reconnect()}>Skúsiť znova</Button>
                    </Warning>
                </div>}
            </div>
        </div>}
    </>;
}
